import {
  Box,
  Button,
  Typography,
} from '@material-ui/core'
import { useCallback, useEffect, useState } from 'react'
import StepsProgress from './steps/StepsProgress'

import ScheduleColumnSelector from './steps/regionOfInterestSelection/scheduleColumnSelection/ScheduleColumnSelector'
import { UIRegionOfInterest } from './steps/regionOfInterestSelection/UIRegionOfInterest'
import ScanMetadataSelection from './steps/scanMetadataSelection/ScanMetadataSelection'

import { FileDetails } from '../../shared/src/data/fileDetails'
import { PageFilter } from '../../shared/src/data/pageFilter'
import { ScanDetails } from '../../shared/src/data/scanDetails'
import {
  scheduleTypeToString,
  stringToScheduleType,
} from '../../shared/src/data/scheduleType'
import FilesPagesSelector from './steps/FilesPagesSelector'
import FilesReorder from './steps/filesReorder/FilesReorder'
import { useSelectedFilesContext } from './selectedFilesProvider'
import { ViewType } from './steps/viewType'
import { useSelectedColumnsContext } from './SelectedColumnsProvider'
import { useFetchFromServer } from './api/fetchHook'
import { serverRoutes } from './layout/serverRoutes'
import {
  DocumentMetadata,
  DocumentOrderHash,
} from '../../shared/src/data/documentMetadata'
import PdfDocumentWrapper from './PdfDocumentWrapper'
import { BoundingBox } from './steps/regionOfInterestSelection/BoundingBox'
import AddExecution from './steps/AddExecution'
import { S3Link } from '../../shared/src/data/s3Link'
import { useInputSelectionStyles } from './InputsSelectionStyles'

const STEP_NAMES = [
  'Set Scan Metadata',
  'Reorder Files',
  'Select Pages',
  'Select Schedule Columns',
  'Add Execution',
]


function flatScheduleColumns(
  columns: Map<number, Map<string, UIRegionOfInterest>>,
): UIRegionOfInterest[] {
  const flatten: UIRegionOfInterest[] = []
  columns.forEach((innerMap) => {
    innerMap.forEach((uiRegionOfInterest) => {
      uiRegionOfInterest.textLabel = scheduleTypeToString(
        uiRegionOfInterest.label,
      )
      flatten.push(uiRegionOfInterest)
    })
  })
  return flatten
}

function recordToUIRegionOfInterest(record: any): UIRegionOfInterest {
  return {
    id: record.id,
    fileId: record.file_id,
    label: stringToScheduleType(record.label),
    textLabel: record.label,
    page: record.page_number,
    regionContentType: record.content_types,
    boundingBoxInNormalizedCoordinates: new BoundingBox(
      {
        x: record.bounding_box.min.x,
        y: record.bounding_box.min.y,
      },
      {
        x: record.bounding_box.max.x,
        y: record.bounding_box.max.y,
      },
    ),
  }
}

function recordsToMap(
  records: any[],
): Map<number, Map<string, UIRegionOfInterest>> {
  const map = new Map<number, Map<string, UIRegionOfInterest>>()
  records.forEach((record) => {
    const parsedRecord = recordToUIRegionOfInterest(record)
    if (!map.has(parsedRecord.page)) {
      map.set(parsedRecord.page, new Map<string, UIRegionOfInterest>())
    }
    map.get(parsedRecord.page)!.set(parsedRecord.id, parsedRecord)
  })
  return map
}

function contentTypeToPageNumber(
  filters: PageFilter[],
  schedulePages: number[],
): Map<string, number[]> {
  const pageIndicesGroupedByContent = new Map<string, Set<number>>()
  filters.forEach((filter) => {
    const contentType = filter.pageContentType

    if (!pageIndicesGroupedByContent.has(contentType)) {
      pageIndicesGroupedByContent.set(contentType, new Set<number>())
    }
    const pageSet = pageIndicesGroupedByContent.get(contentType)!
    filter.pageNumbers.forEach((pageNumber) => pageSet.add(pageNumber))
  })

  pageIndicesGroupedByContent.set(ViewType.schedule, new Set<number>())
  schedulePages.forEach((page) => {
    const pageSet = pageIndicesGroupedByContent.get(ViewType.schedule)!
    pageSet.add(page)
  })

  const result = new Map<string, number[]>()
  pageIndicesGroupedByContent.forEach((pageSet, contentType) => {
    const pagesArray = Array.from(pageSet)
    if (pagesArray.length > 0) {
      result.set(contentType, pagesArray)
    }
  })
  return result
}

export function documentMetadata(
  files: PdfDocumentWrapper[],
): DocumentMetadata[] {
  return (files || []).map<DocumentMetadata>((doc) => ({
    fileId: doc.id,
    fileName: doc.name,
    totalNumOfPages: doc.document.numPages,
  }))
}

interface ExecutionParamsResponse {
  pages: Record<string, number[]>
  schedule_columns: UIRegionOfInterest[]
}

export default function InputsSelectionProcess(): JSX.Element {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
  const [isCurrentStepValid, setIsCurrentStepValid] = useState<boolean>(false)
  const [selectedScan, setSelectedScan] = useState<ScanDetails>()
  const [pageFilters, setPageFilters] = useState<PageFilter[]>([])
  const classes = useInputSelectionStyles()
  const { files } = useSelectedFilesContext()
  const { pageToSelectedColumns, setPageToSelectedColumnsMap } =
    useSelectedColumnsContext()

  const { fetchFromServer, fetchJsonFromServer } = useFetchFromServer()
  const saveExecutionParams = async () : Promise<S3Link | undefined> => {
    try {
      const documents = documentMetadata(files)
      const contentTypes = contentTypeToPageNumber(
        pageFilters,
        Array.from(pageToSelectedColumns.keys()),
      )
      const response = await fetchFromServer(
        serverRoutes.api.executionParams.post(
          selectedScan!.project.id,
          selectedScan!.id,
          filesHash,
        ),
        {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            schedules: flatScheduleColumns(pageToSelectedColumns),
            contentTypes: Object.fromEntries(contentTypes.entries()),
            documents: documents,
          }),
        },
      )
      if (!response.ok) throw new Error('Failed to save data')

      const data = await response.json()
      return data.executionParamsUrl as S3Link
    } catch (error) {
      console.error(error)
    }
  }

  const [filesHash, setFilesHash] = useState<string>(
    DocumentOrderHash(documentMetadata(files)),
  )
  const [viewTypesPages, setViewTypesPages] = useState<Map<ViewType, number[]>>(
    new Map(),
  )
  const loadExecutionParameters = useCallback(
    async (currentFilesHash) => {
      try {
        console.log('Loading execution parameters', currentFilesHash)
        const response = await fetchJsonFromServer(
          serverRoutes.api.executionParams.get(
            selectedScan!.project.id,
            selectedScan!.id,
            currentFilesHash,
          ),
        )

        const data = response as ExecutionParamsResponse
        if (!data) {
          setViewTypesPages(new Map())
          setPageToSelectedColumnsMap(new Map())
          return
        }

        const contentTypesMap = new Map<ViewType, number[]>(
          Object.values(ViewType).map((viewType) => [
            viewType,
            data.pages[viewType] || []
          ]),
        )
        const schedulesMap = recordsToMap(data.schedule_columns)
        setViewTypesPages(contentTypesMap)
        setPageToSelectedColumnsMap(schedulesMap)
      } catch (error) {
        console.error(error)
      }
    },
    [selectedScan, fetchJsonFromServer, setPageToSelectedColumnsMap],
  )

  const [filesDetails, setFilesDetails] = useState<FileDetails[] | undefined>(
    undefined,
  )
  useEffect(() => {
    const orderHash = DocumentOrderHash(documentMetadata(files))
    if (filesHash !== orderHash) {
      setFilesHash(orderHash)
      loadExecutionParameters(orderHash)
    }
  }, [loadExecutionParameters, setFilesHash, files, filesHash])

  return (
    <div className={classes.root}>
      <div>
        <StepsProgress
          stepNames={STEP_NAMES}
          activeStepIndex={activeStepIndex}
        />
        <div className={classes.stepArea}>
          <div className={classes.buttons}>
            <Button
              variant='contained'
              color='primary'
              disabled={ activeStepIndex === 0 }
              onClick={() => setActiveStepIndex(activeStepIndex - 1)}
            >
              Back
            </Button>
            <Typography variant='h5'>{STEP_NAMES[activeStepIndex]}</Typography>
            {activeStepIndex === STEP_NAMES.length - 1 ? (
              <Button
                variant='contained'
                color='primary'
                disabled={!isCurrentStepValid}
                onClick={() => setActiveStepIndex(0)}
              >
                Restart
              </Button>
            ) : (
              <div
                  style={{
                    display: 'inline-block',
                    cursor: 'pointer',
                  }}
                >
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setActiveStepIndex(activeStepIndex + 1)}
                    style={{ pointerEvents: 'auto' }}
                    disabled={!isCurrentStepValid}
                  >
                    Next
                  </Button>
                </div>
            )}
          </div>
          <Box className={classes.step} boxShadow={1}>
            {[
              <ScanMetadataSelection
                selectedScan={selectedScan}
                setSelectedScan={setSelectedScan}
                setFileDetails={setFilesDetails}
                isStepValid={setIsCurrentStepValid}
                fileDetails={filesDetails}
              />,
              <FilesReorder
                selectedScan={selectedScan}
                setIsStepValid={setIsCurrentStepValid}
                fileDetails={filesDetails || []}
              />,
              <FilesPagesSelector
                viewTypesPages={viewTypesPages}
                setIsStepValid={setIsCurrentStepValid}
                setFilesPageFilters={setPageFilters}
                saveExecutionParams={saveExecutionParams}
              />,
              <ScheduleColumnSelector
                saveExecutionParams={saveExecutionParams}
              />,
              <AddExecution scan={selectedScan} saveExecutionParams={saveExecutionParams}/>,
            ][activeStepIndex] || <div>Invalid Step Error!</div>}
          </Box>
        </div>
      </div>
    </div>
  )
}
